<template>
  <modal title="Invite people to North" :show="show" @hidden="closeModal" :hide-footer="true">
    <div class="d-flex flex-column">
      <label class="mb-2">Invite team members via email</label>
      <div v-for="(email, index) of emails" :key="index" class="ml-n2">
        <form-input
          key="email"
          id="email"
          type="email"
          placeholder="Enter Email id"
          v-model="emails[index]"
          :errored="!!getError(index)"
          :error-message="getError(index)"
          :autofocus="true"
        />
      </div>
      <div>
        <b-button variant="link" class="text-secondary text-medium-1 pl-0" @click="addUser"
          >+ Invite more
        </b-button>
      </div>
    </div>
    <div class="send-invites">
      <submit-buttons
        :hide-cancel="true"
        submit-text="Send Invites"
        :is-loading="isSubmitting"
        @save="onSubmit"
      />
    </div>
    <max-users-paywall-modal
      v-model="isPaywallModalShown"
      :org-id="orgId"
      v-if="isPaywallModalShown"
    />
  </modal>
</template>

<script>
import FormInput from '@/components/form/FormInput.vue'
import Modal from '@/components/Modal'
import MaxUsersPaywallModal from '@/components/paywalls/MaxUsersPaywallModal.vue'
import SubmitButtons from '@/components/SubmitButtons'
import { featureFlags } from '@/constants/featureFlags'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import EventBusMixin from '@/mixins/EventBusMixin'
import FeaturesMixin from '@/mixins/FeaturesMixin'
import getPlanDisplayName from '@/utils/getPlanDisplayName'
import { BButton } from 'bootstrap-vue'
import * as R from 'ramda'
import { mapActions } from 'vuex'

/**
 * This component shows up in a modal. It can be used to manage organization membership
 */
export default {
  name: 'OrgInviteModal',
  mixins: [FeaturesMixin, EventBusMixin],
  props: {
    show: Boolean,
    orgId: [Number, String]
  },
  model: {
    prop: 'show'
  },
  components: {
    Modal,
    BButton,
    SubmitButtons,
    MaxUsersPaywallModal,
    FormInput
  },
  data() {
    return {
      emails: { 0: '' },
      userCount: 1,
      errors: {},
      isSubmitting: false,
      isPaywallModalShown: false,
      isInviteAllowed: true
    }
  },
  computed: {
    org() {
      return this.$store.getters.orgById(this.orgId)
    }
  },
  methods: {
    ...mapActions(['inviteUsers', 'getOrgMemberships']),
    getError(index) {
      return this.errors?.emails?.[index]?.[0]
    },
    getPlanDisplayName,
    showPaywall() {
      this.isPaywallModalShown = true
    },
    async onSubmit() {
      const emailsToInvite = Object.values(this.emails).filter(Boolean)
      if (emailsToInvite.length === 0) {
        this.errors = {
          emails: [['Enter a valid email address']]
        }
        return
      }
      if (!this.isInviteAllowed) {
        this.showPaywall()
        return
      }
      try {
        this.isSubmitting = true
        await this.inviteUsers({ orgId: this.orgId, emails: emailsToInvite })
        EventBus.$emit('ADD_TOAST', { message: 'Invites sent successfully' })
        this.closeModal()
      } catch (e) {
        let errors = R.path(['response', 'data', 'detail'], e)
        if (errors) {
          this.handleInviteErrors(errors)
        } else {
          // handle other cases when detail is not present
          EventBus.$emit('ADD_TOAST', { message: DEFAULT_ERROR, type: TOAST_TYPE.ERROR })
        }
      }
      this.isSubmitting = false
    },
    handleInviteErrors(errors) {
      if (errors.emails) {
        // re-map indices, since we are remove empty email fields before sending it server
        const nonEmptyEmails = R.toPairs(this.emails).filter(R.nth(1))
        const emailErrors = R.toPairs(errors.emails).reduce((emailErrors, indexErrorPair) => {
          const actualIndex = nonEmptyEmails[indexErrorPair[0]][0]
          emailErrors[actualIndex] = indexErrorPair[1]
          return emailErrors
        }, {})
        this.errors = { emails: emailErrors }
      } else {
        errors = typeof errors === 'string' ? [errors] : errors
        this.emitErrors(errors)
      }
    },
    addUser() {
      this.emails = { ...this.emails, [this.userCount++]: '' }
    },
    closeModal() {
      this.emails = { 0: '' }
      this.$emit('input', false)
    },
    emitErrors(errors) {
      errors.forEach(error => {
        EventBus.$emit('ADD_TOAST', {
          message: error,
          type: TOAST_TYPE.ERROR
        })
      })
    }
  },
  async mounted() {
    this.listenToEventBus('SUBMIT_FORM', () => {
      this.show && this.onSubmit()
    })
    this.isInviteAllowed = await this.satisfyFeature(this.orgId, featureFlags.MAX_USER_COUNT)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/forms';
@import '~@/styles/_errors.scss';

.added-members {
  padding: 0;
}

.send-invites {
  padding-bottom: 6px;
}
</style>
